import { useCallback } from "react";
import { UploadSource } from "@/components/upload/types/upload-item";
import { logger } from "@/lib/logger";
import { useSharePointContext } from "../providers/SharePointContext";
import useAzureStorage from "./useAzureStorage";

export type CloudSharePointIds = {
  driveId: string;
  siteId: string;
  sharepointFileId: string;
  source: UploadSource;
};

export type CloudDocxSharePointIds = {
  driveId: string;
  siteId: string;
  sharepointFileId: string;
};

export const isCloudDocxFile = (
  fileName: string,
  source: UploadSource,
  ids?: {
    driveId?: string;
    siteId?: string;
    sharepointFileId?: string;
  }
): ids is CloudDocxSharePointIds => {
  if (!fileName.toLowerCase().endsWith(".docx")) return false;
  if (source === UploadSource.LOCAL) return false;
  return Boolean(ids?.driveId && ids?.siteId && ids?.sharepointFileId);
};

/**
 * Stages a Microsoft Graph PDF into the workspace-documents blob (same name as DOCX entityId)
 * so the indexer can skip Gotenberg. Failures are logged; indexing falls back to Gotenberg.
 */
export const useMicrosoftDocxPdfStaging = () => {
  const { fetchGraphPdfForCloudItem } = useSharePointContext();
  const { uploadPdfToWorkspaceBlob } = useAzureStorage();

  const stageMicrosoftPdfIfNeeded = useCallback(
    async (
      fileName: string,
      blobName: string,
      workspaceId: string,
      ids?: {
        driveId?: string;
        siteId?: string;
        sharepointFileId?: string;
        source?: UploadSource;
      }
    ): Promise<boolean> => {
      const operation = "stageMicrosoftPdf";
      const source = ids?.source ?? UploadSource.LOCAL;

      const cloudIds = {
        driveId: ids?.driveId,
        siteId: ids?.siteId,
        sharepointFileId: ids?.sharepointFileId,
      };

      if (!isCloudDocxFile(fileName, source, cloudIds)) {
        return false;
      }

      const { driveId, siteId, sharepointFileId } = cloudIds;

      const pdfBlob = await fetchGraphPdfForCloudItem({
        driveId,
        siteId,
        sharepointFileId,
      });

      if (!pdfBlob) {
        logger.warn("Skipping workspace PDF staging; Graph PDF unavailable", {
          operation,
          blobName,
          workspaceId,
        });
        return false;
      }

      const uploaded = await uploadPdfToWorkspaceBlob(
        pdfBlob,
        blobName,
        workspaceId,
        fileName
      );

      if (uploaded) {
        logger.info("Staged Microsoft Graph PDF in workspace blob", {
          operation,
          blobName,
          workspaceId,
        });
      }

      return uploaded;
    },
    [fetchGraphPdfForCloudItem, uploadPdfToWorkspaceBlob]
  );

  return { stageMicrosoftPdfIfNeeded };
};
